/**
 * Audit Trail Service
 * 
 * Audit trail API operations for fetching and managing audit logs.
 */

import { apiClient } from '../client';
import type { PaginationParams, DateRangeFilter } from '../types';

export type AuditAction = 'INSERT' | 'UPDATE' | 'DELETE' | 'RESTORE';

export interface AuditEntry {
  id: string;
  organizationId: string;
  tableName: string;
  recordId: string;
  action: AuditAction;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  changedBy: string | null;
  changedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface AuditListResponse {
  entries: AuditEntry[];
  pagination: {
    limit: number;
    offset: number;
    count: number;
  };
}

export interface AuditEntryResponse {
  entry: AuditEntry;
}

export interface RecordHistoryResponse {
  tableName: string;
  recordId: string;
  history: AuditEntry[];
  count: number;
}

export interface RecordSnapshotsResponse {
  tableName: string;
  recordId: string;
  snapshots: Array<{
    version: number;
    data: Record<string, unknown>;
    changedAt: string;
    changedBy: string | null;
  }>;
  count: number;
}

export interface UserChangesResponse {
  userId: string;
  changes: AuditEntry[];
  count: number;
}

export interface AuditSummaryResponse {
  today: number;
  byAction: Array<{ action: string; count: number }>;
  topUsers: Array<{ changed_by: string; count: number }>;
}

export interface AuditFilters extends PaginationParams, DateRangeFilter {
  organizationId: string;
  tableName?: string;
  recordId?: string;
  action?: AuditAction;
  changedBy?: string;
}

export class AuditService {
  /**
   * List audit log entries with filters
   */
  async list(filters: AuditFilters): Promise<AuditListResponse> {
    const params = new URLSearchParams();
    params.append('organization_id', filters.organizationId);
    
    if (filters.tableName) params.append('table_name', filters.tableName);
    if (filters.recordId) params.append('record_id', filters.recordId);
    if (filters.action) params.append('action', filters.action);
    if (filters.changedBy) params.append('changed_by', filters.changedBy);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset) params.append('offset', String(filters.offset));

    return apiClient.get<AuditListResponse>(`/audit-log?${params.toString()}`);
  }

  /**
   * Get specific audit entry by ID
   */
  async get(id: string, organizationId: string): Promise<AuditEntry> {
    const response = await apiClient.get<AuditEntryResponse>(`/audit-log/${id}?organization_id=${organizationId}`);
    return response.entry;
  }

  /**
   * Get record history (all changes for a specific record)
   */
  async getRecordHistory(
    tableName: string,
    recordId: string,
    organizationId: string
  ): Promise<RecordHistoryResponse> {
    return apiClient.get<RecordHistoryResponse>(
      `/audit-log/record/${tableName}/${recordId}?organization_id=${organizationId}`
    );
  }

  /**
   * Get record snapshots (all versions of a record)
   */
  async getRecordSnapshots(
    tableName: string,
    recordId: string,
    organizationId: string
  ): Promise<RecordSnapshotsResponse> {
    return apiClient.get<RecordSnapshotsResponse>(
      `/audit-log/snapshots/${tableName}/${recordId}?organization_id=${organizationId}`
    );
  }

  /**
   * Get changes made by a specific user
   */
  async getUserChanges(
    userId: string,
    organizationId: string,
    limit?: number
  ): Promise<UserChangesResponse> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (limit) params.append('limit', String(limit));

    return apiClient.get<UserChangesResponse>(
      `/audit-log/user/${userId}?${params.toString()}`
    );
  }

  /**
   * Get audit summary for dashboard
   */
  async getDashboardSummary(organizationId: string): Promise<AuditSummaryResponse> {
    return apiClient.get<AuditSummaryResponse>(
      `/audit-log/summary/dashboard?organization_id=${organizationId}`
    );
  }

  /**
   * Export audit log to PDF
   */
  async exportToPDF(filters: Omit<AuditFilters, 'limit' | 'offset'>): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('organization_id', filters.organizationId);
    
    if (filters.tableName) params.append('table_name', filters.tableName);
    if (filters.recordId) params.append('record_id', filters.recordId);
    if (filters.action) params.append('action', filters.action);
    if (filters.changedBy) params.append('changed_by', filters.changedBy);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    params.append('format', 'pdf');

    const response = await fetch(`${apiClient['baseUrl']}/audit-log/export?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to export audit log to PDF');
    }

    return response.blob();
  }

  /**
   * Export audit log to Excel
   */
  async exportToExcel(filters: Omit<AuditFilters, 'limit' | 'offset'>): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('organization_id', filters.organizationId);
    
    if (filters.tableName) params.append('table_name', filters.tableName);
    if (filters.recordId) params.append('record_id', filters.recordId);
    if (filters.action) params.append('action', filters.action);
    if (filters.changedBy) params.append('changed_by', filters.changedBy);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    params.append('format', 'excel');

    const response = await fetch(`${apiClient['baseUrl']}/audit-log/export?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to export audit log to Excel');
    }

    return response.blob();
  }

  private getAuthToken(): string | null {
    const match = document.cookie.match(new RegExp('kirjuri_session=([^;]+)'));
    return match ? match[1] : null;
  }
}

// Export singleton instance
export const auditService = new AuditService();
